/**
 * The growth console's reads — `/v1/admin/analytics/*`.
 *
 * **Both endpoints read the nightly rollup, never raw events.** That is a server-side property,
 * but it shapes this file too: there is no hook here that fetches a list of people, because the
 * API deliberately offers none. Journey events are pseudonymous and the console does not pierce
 * that; to act on an individual you work from the booking or the support ticket.
 *
 * **Filtering happens on the server.** A client-side `.filter()` over a window would search only
 * the rows already fetched and report confidently that a city had no traffic.
 */
import { useQuery } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type FunnelResponse = components['schemas']['FunnelResponse'];
export type FunnelStep = components['schemas']['FunnelStep'];
export type MetricsResponse = components['schemas']['MetricsResponse'];
export type MetricPoint = components['schemas']['MetricPoint'];

export interface AnalyticsWindow extends Record<string, unknown> {
  date_from: string;
  date_to: string;
  city_id?: number;
  puja_id?: number;
}

/** One grain at a time: a window filtered by both a city and a puja reads as a fact about
 * neither, so the screens clear one when the other is set and the query key follows. */
function windowQuery(window: AnalyticsWindow): string {
  const search = new URLSearchParams({
    date_from: window.date_from,
    date_to: window.date_to,
  });
  if (window.city_id !== undefined) search.set('city_id', String(window.city_id));
  if (window.puja_id !== undefined) search.set('puja_id', String(window.puja_id));
  return search.toString();
}

export type MetricCatalogue = components['schemas']['MetricCatalogue'];
export type EventHealthResponse = components['schemas']['EventHealthResponse'];

/** Today's accepted and rejected counts per event, as ingestion tallied them.
 *
 * Counts only — never the rejection reasons, which stay in the server log. Whether a row is over
 * the line is the server's verdict (`above_threshold`), not a comparison made here, so the
 * console and the setting cannot disagree about what "exactly the threshold" means.
 */
export function useEventHealth() {
  return useQuery({
    queryKey: keys.analytics.eventHealth(),
    queryFn: () => authedClient.request<EventHealthResponse>('/v1/admin/analytics/event-health'),
  });
}

/** Every key the rollup writes, from the server.
 *
 * The console used to keep this list itself and it was wrong in both directions — offering
 * `dau.devotee` and `attribution.deeplink_open`, which nothing emits, while missing the real
 * keys. A key nobody writes looks exactly like a metric that has gone to zero, which is the
 * worst way for a dashboard to be wrong.
 */
export function useMetricKeys() {
  return useQuery({
    queryKey: [...keys.analytics.all(), 'metric-keys'] as const,
    queryFn: () => authedClient.request<MetricCatalogue>('/v1/admin/analytics/metric-keys'),
  });
}

/**
 * Rollup-backed reads change once a day (the 02:00 IST job), so an hour fresh is honest; the
 * screens carry a manual Refresh for the operator who knows a backfill just ran (doc 05 §2).
 */
export const ROLLUP_STALE_MS = 60 * 60 * 1000;

export function useFunnel(window: AnalyticsWindow) {
  return useQuery({
    queryKey: keys.analytics.funnel(window),
    queryFn: () =>
      authedClient.request<FunnelResponse>(`/v1/admin/analytics/funnel?${windowQuery(window)}`),
    staleTime: ROLLUP_STALE_MS,
  });
}

export function useMetrics(window: AnalyticsWindow, metricKeys: readonly string[]) {
  const params = { ...window, metric_key: metricKeys.join(',') };
  return useQuery({
    queryKey: keys.analytics.metrics(params),
    queryFn: () =>
      authedClient.request<MetricsResponse>(
        `/v1/admin/analytics/metrics?${windowQuery(window)}&metric_key=${encodeURIComponent(
          metricKeys.join(','),
        )}`,
      ),
    // A key list nobody picked is a request the server would refuse. Not asking is the honest
    // shape for "the operator has not chosen yet".
    enabled: metricKeys.length > 0,
    staleTime: ROLLUP_STALE_MS,
  });
}
